import React, {ForwardedRef, forwardRef, useCallback, useEffect, useMemo, useRef} from "react";
import {LiveblogTeaserCoreWidgetProps, LiveblogTeaserWidgetApi} from "./LiveblogTeaserWidgetProps";
import {
  DefaultConsentProvider,
  LiveblogEvent,
  LiveblogRenderContext,
  LiveblogRenderContextProvider,
  TeaserWrapper,
  TrackingFunction
} from "@webng/liveblog";
import {TeaserWidget} from "@webng/liveblog/src/teaser-widget/TeaserWidget";
import {buildAnalyticsTeaserThemeConfiguration} from "@webng-types/embedjs";
import * as externalUserProvider from "../user";
import {IEvent} from "@webng-types/write-model";
import {useLiveblogTeaserAnalyticsAgent} from "./useLiveblogTeaserAnalyticsAgent";
import {fireTickarooLiveblogTeaserInitEvent, fireTickarooLiveblogTeaserTracking} from "../events";
import {useTeaserWidgetEvents} from "@webng/liveblog/src/teaser-widget/useTeaserWidgetEvents";

type LiveblogTeaserCoreControllerProps = Omit<LiveblogTeaserCoreWidgetProps, "locale">

function LiveblogTeaserWidgetCoreImp({ liveblogId, themeId, sharedThemeConfiguration, liveblogTeaserThemeConfiguration, css, initialData, disableTracking, liveblogUrl, title }: LiveblogTeaserCoreControllerProps, ref: ForwardedRef<LiveblogTeaserWidgetApi>) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const agent = useLiveblogTeaserAnalyticsAgent(initialData.gameShowResponse.game?.ticker_id ?? liveblogId, sharedThemeConfiguration.useCookies === 'all', disableTracking);

  // Held in a ref so the once-per-load t_ini effect can read the resolved theme without taking the
  // config objects as effect dependencies (which would re-fire the load event).
  const analyticsTheme = useMemo(
    () => buildAnalyticsTeaserThemeConfiguration(liveblogTeaserThemeConfiguration, sharedThemeConfiguration, themeId ?? ""),
    [liveblogTeaserThemeConfiguration, sharedThemeConfiguration, themeId]
  )
  const analyticsThemeRef = useRef(analyticsTheme)
  analyticsThemeRef.current = analyticsTheme

  const trackingFunction: TrackingFunction = useCallback((e: LiveblogEvent) => {
    if(agent) {
      agent.track(e)
      if (wrapperRef.current && wrapperRef.current.parentElement) {
        fireTickarooLiveblogTeaserTracking(wrapperRef.current.parentElement, e)
      }
    }
  }, [agent])

  // dispatch init event
  useEffect(() => {
    if (wrapperRef.current && wrapperRef.current.parentElement) {
      fireTickarooLiveblogTeaserInitEvent(wrapperRef.current.parentElement, { liveblogId: liveblogId })
    }
  }, [liveblogId])

  useEffect(() => {
    trackingFunction({ t: 't_ini', th: analyticsThemeRef.current })
  }, [trackingFunction])

  const renderContext: Partial<LiveblogRenderContext> = useMemo(() => {
    const useFunctionalCookies = sharedThemeConfiguration.useCookies === 'all' || sharedThemeConfiguration.useCookies === 'functional'
    return {
      consentProvider: new DefaultConsentProvider(useFunctionalCookies),
      webEmbedDefaultProviderConstraint: 'disabled',
      webEmbedProviderConstraints: {},
      showEventSharing: 'off',
      layoutAuthorPosition: 'off',
      commentsMode: 'disabled',
      externalUserProvider: externalUserProvider,
      uniqueId: initialData.uniqueId,
      useFunctionalCookies,
      trackingFunction: trackingFunction
    }
  }, [sharedThemeConfiguration.useCookies, initialData.uniqueId, trackingFunction]);


  const onClick = useCallback((event: IEvent) => {
    trackingFunction({t: 'a_tli', m: event.local_id})
  }, [trackingFunction])

  const onGotoLiveblogClick = useCallback((e: React.MouseEvent) => {
    trackingFunction({t: 'a_tla'})
  }, [trackingFunction])

  const {game, events} = useTeaserWidgetEvents({gameShowResponse: initialData.gameShowResponse, content: liveblogTeaserThemeConfiguration.content, limit: liveblogTeaserThemeConfiguration.limit})

  return <LiveblogRenderContextProvider context={renderContext}>
    <TeaserWrapper
      ref={wrapperRef}
      liveblogId={liveblogId}
      invertTime={liveblogTeaserThemeConfiguration.styleInvertTime}
      layout={liveblogTeaserThemeConfiguration.layout}
      layoutLiveIndicatorStyle={liveblogTeaserThemeConfiguration.layoutLiveIndicatorStyle}
      colorScheme={sharedThemeConfiguration.colorScheme}
      layoutTimeline={liveblogTeaserThemeConfiguration.layoutTimeline}
      styleTextColor={liveblogTeaserThemeConfiguration.styleTextColor}
      styleTextColorDark={liveblogTeaserThemeConfiguration.styleTextColorDark}
      styleTextMutedColor={liveblogTeaserThemeConfiguration.styleTextMutedColor}
      styleTextMutedColorDark={liveblogTeaserThemeConfiguration.styleTextMutedColorDark}
      styleBgColor={liveblogTeaserThemeConfiguration.styleBgColor}
      styleBgColorDark={liveblogTeaserThemeConfiguration.styleBgColorDark}
      styleLinkColor={liveblogTeaserThemeConfiguration.styleLinkColor}
      styleLinkColorDark={liveblogTeaserThemeConfiguration.styleLinkColorDark}
      styleSurfaceColor={liveblogTeaserThemeConfiguration.styleSurfaceColor}
      styleSurfaceColorDark={liveblogTeaserThemeConfiguration.styleSurfaceColorDark}
      css={css}
      numberOfEvents={events.length}>

      <TeaserWidget
        title={title}
        liveblogUrl={liveblogUrl}
        onGotoLiveblogClick={onGotoLiveblogClick}
        layoutLiveIndicatorStyle={liveblogTeaserThemeConfiguration.layoutLiveIndicatorStyle}
        layoutLiveblogLink={liveblogTeaserThemeConfiguration.layoutLiveblogLink}
        layout={liveblogTeaserThemeConfiguration.layout}
        layoutWhenScoreboard={liveblogTeaserThemeConfiguration.layoutWhenScoreboard}
        showTitle={liveblogTeaserThemeConfiguration.showTitle}
        limit={liveblogTeaserThemeConfiguration.limit}
        game={game}
        events={events}
        onItemClick={onClick}
      />

    </TeaserWrapper>
  </LiveblogRenderContextProvider>
}

export const LiveblogTeaserWidgetCore = forwardRef<LiveblogTeaserWidgetApi, LiveblogTeaserCoreControllerProps>(LiveblogTeaserWidgetCoreImp)
